<script setup lang="ts">
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from 'reka-ui';
import { computed } from 'vue';
import { X } from 'lucide-vue-next';
import { cn } from '@/lib/cn';

const props = defineProps<{
  open: boolean;
  side?: 'right' | 'left' | 'top' | 'bottom';
  title?: string;
  description?: string;
  class?: string;
  hideClose?: boolean;
  widthClass?: string;
}>();

const emit = defineEmits<{ 'update:open': [value: boolean] }>();

const sideClasses = computed(() => {
  switch (props.side ?? 'right') {
    case 'left':
      return 'inset-y-0 left-0 h-full border-r data-[state=open]:animate-slide-in-right';
    case 'top':
      return 'inset-x-0 top-0 w-full border-b';
    case 'bottom':
      return 'inset-x-0 bottom-0 w-full border-t';
    default:
      return 'inset-y-0 right-0 h-full border-l data-[state=open]:animate-slide-in-right';
  }
});

const widthClasses = computed(() => {
  if (props.side === 'top' || props.side === 'bottom') return '';
  return props.widthClass ?? 'w-full max-w-md';
});
</script>

<template>
  <DialogRoot :open="props.open" @update:open="(v: boolean) => emit('update:open', v)">
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm data-[state=open]:animate-fade-in"
      />
      <DialogContent
        :class="cn(
          'fixed z-50 bg-surface shadow-pop flex flex-col gap-4 outline-none',
          sideClasses,
          widthClasses,
          props.class,
        )"
      >
        <header v-if="title || $slots.header" class="flex items-start justify-between gap-4 px-6 pt-6">
          <div class="flex flex-col gap-1">
            <DialogTitle v-if="title" class="text-display-sm text-text">{{ title }}</DialogTitle>
            <DialogDescription v-if="description" class="text-caption text-text-muted">
              {{ description }}
            </DialogDescription>
            <slot name="header" />
          </div>
          <DialogClose
            v-if="!hideClose"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-bg hover:text-text transition-colors"
            aria-label="Close"
          >
            <X class="h-4 w-4" />
          </DialogClose>
        </header>
        <div class="flex-1 overflow-y-auto px-6 pb-6">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="border-t border-border px-6 py-4 flex items-center gap-2">
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
